
import { useState } from 'react';
import HolographicCard from '@/components/base/HolographicCard';

export default function VerificationSection() {
  const [verificationCode, setVerificationCode] = useState('');
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate verification process
    setTimeout(() => {
      setIsVerified(true);
      setIsLoading(false);
    }, 2000);
  };

  const handleResendCode = () => {
    // Simulate resending verification code
    alert('Verification code has been resent to your email.');
  };

  if (isVerified) {
    return (
      <section className="relative py-32 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="mb-12">
            <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center bg-green-500/20 rounded-full">
              <i className="ri-check-line text-4xl text-green-400"></i>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Verification Complete
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Your account has been successfully verified. You now have access to Fieldscale's advanced AI technologies.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-colors whitespace-nowrap">
              Access Dashboard
            </button>
            <button className="px-8 py-4 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap">
              Download Resources
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-32 px-6">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Account Verification
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Please enter the verification code sent to your email to complete your account setup and gain access to Fieldscale's AI technologies.
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <HolographicCard className="p-8">
            <form onSubmit={handleVerification} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-white/80 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  id="code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm text-center tracking-widest"
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !verificationCode || !email}
                className="w-full px-6 py-3 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify Account'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-white/60 text-sm mb-3">
                Didn't receive the code?
              </p>
              <button
                onClick={handleResendCode}
                className="text-white hover:text-white/80 transition-colors text-sm underline whitespace-nowrap"
              >
                Resend verification code
              </button>
            </div>

            <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-white/60 mt-0.5"></i>
                <div className="text-sm text-white/60">
                  <p className="font-medium mb-1">Security Notice</p>
                  <p>Your verification code expires in 10 minutes. For security purposes, please do not share this code with anyone.</p>
                </div>
              </div>
            </div>
          </HolographicCard>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-white/10 rounded-lg">
              <i className="ri-shield-check-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Secure Access</h3>
            <p className="text-white/60 text-sm">
              Multi-factor authentication ensures your account remains protected
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-white/10 rounded-lg">
              <i className="ri-cpu-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">AI Technologies</h3>
            <p className="text-white/60 text-sm">
              Access to Precursor OS and AlphaSigma™ AI engine upon verification
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-white/10 rounded-lg">
              <i className="ri-support-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">24/7 Support</h3>
            <p className="text-white/60 text-sm">
              Our technical team is available to assist with any verification issues
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
